import React, { createContext, useContext, useState, useCallback } from 'react';

export interface BreadcrumbSeg { label: string; onClick?: () => void; }

export interface PublishConfig {
  onPublish: () => void;
  disabled: boolean;
  publishState: 'idle' | 'loading' | 'ok' | 'err';
}

export interface CommandsConfig {
  pendingCount: number;
  onOpen: () => void;
}

/** Primary page action hosted by the topbar (used on mobile, where the in-page
 *  toolbars are too cramped to carry it). */
export interface ActionConfig {
  label: string;
  onClick: () => void;
  disabled?: boolean;
}

export interface SaveConfig {
  /** There are changes since the last save — drives the highlighted look. */
  dirty: boolean;
  /** Clickable even when not dirty (e.g. re-publishing). Defaults to `dirty`. */
  enabled?: boolean;
  saving?: boolean;
  saved?: boolean;
  /** Shown as a tooltip, e.g. why the button is inactive. */
  hint?: string;
  onSave: () => void;
}

interface PageHeaderCtx {
  breadcrumbs: BreadcrumbSeg[];
  setBreadcrumbs: (segs: BreadcrumbSeg[]) => void;
  publishConfig: PublishConfig | null;
  setPublishConfig: (config: PublishConfig | null) => void;
  commandsConfig: CommandsConfig | null;
  setCommandsConfig: (config: CommandsConfig | null) => void;
  saveConfig: SaveConfig | null;
  setSaveConfig: (config: SaveConfig | null) => void;
  actionConfig: ActionConfig | null;
  setActionConfig: (config: ActionConfig | null) => void;
}

const PageHeaderContext = createContext<PageHeaderCtx>({
  breadcrumbs: [],
  setBreadcrumbs: () => {},
  publishConfig: null,
  setPublishConfig: () => {},
  commandsConfig: null,
  setCommandsConfig: () => {},
  saveConfig: null,
  setSaveConfig: () => {},
  actionConfig: null,
  setActionConfig: () => {},
});

export const PageHeaderProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [breadcrumbs, setBreadcrumbs] = useState<BreadcrumbSeg[]>([]);
  const [publishConfig, setPublishConfig] = useState<PublishConfig | null>(null);
  const [commandsConfig, setCommandsConfig] = useState<CommandsConfig | null>(null);
  const [saveConfig, setSaveConfig] = useState<SaveConfig | null>(null);
  const [actionConfig, setActionConfig] = useState<ActionConfig | null>(null);
  const set    = useCallback((segs: BreadcrumbSeg[]) => setBreadcrumbs(segs), []);
  const setPub = useCallback((cfg: PublishConfig | null) => setPublishConfig(cfg), []);
  const setCmd = useCallback((cfg: CommandsConfig | null) => setCommandsConfig(cfg), []);
  const setSave = useCallback((cfg: SaveConfig | null) => setSaveConfig(cfg), []);
  const setAction = useCallback((cfg: ActionConfig | null) => setActionConfig(cfg), []);
  return (
    <PageHeaderContext.Provider value={{
      breadcrumbs, setBreadcrumbs: set,
      publishConfig, setPublishConfig: setPub,
      commandsConfig, setCommandsConfig: setCmd,
      saveConfig, setSaveConfig: setSave,
      actionConfig, setActionConfig: setAction,
    }}>
      {children}
    </PageHeaderContext.Provider>
  );
};

export const usePageHeader = () => useContext(PageHeaderContext);
